import { showSuccess } from "actions/notification";
import {
  create,
  destroy,
  getPortfolios as retrievePortfolios,
  requestAccess,
  update,
} from "api/portfolio";
import { create as createSubscription, remove as removeSubscription } from "api/subscription";
import { push } from "connected-react-router";
import intersection from "lodash/intersection";
import isEqual from "lodash/isEqual";
import { isAssetIndex, isAssetLeadIndex, isDealIndex } from "selectors/location";

import { DataStorage } from "utils";
import Translate from "utils/translate";
import { createUrlParams, getUrlParams } from "utils/urlParams";

const { t } = new Translate("notifications");

const getDealPath = (params) => `/deals${createUrlParams(params)}`;
const getAssetPath = (params) => `/assets${createUrlParams(params)}`;
const getAssetLeadPath = (params) => `/asset_leads${createUrlParams(params)}`;

const getNextPath = (pathname, portfolioIDs) => {
  const params = { ...getUrlParams(), portfolioIDs };
  if (pathname.startsWith("/deals")) return getDealPath(params);
  if (pathname.startsWith("/asset_leads")) return getAssetLeadPath(params);

  return getAssetPath(params);
};
const shouldSwitchPortfolios = (state, portfolioIDs) =>
  !isEqual(state.selectedPortfolioIDs, portfolioIDs) ||
  !(isDealIndex(state) || isAssetIndex(state)) ||
  isAssetLeadIndex(state);

export const closePortfolioSidebar = () => ({
  type: "SIDEBAR_CLOSE",
});

export const openPortfolioSidebar = () => ({
  type: "SIDEBAR_OPEN",
});

export const getPortfolios = () => (dispatch, getState) => {
  if (getState().portfolios.length) return Promise.resolve();

  return dispatch({
    type: "PORTFOLIOS_GET",
    payload: retrievePortfolios(),
  });
};

export const requestPortfolioAccess = (portfolioID) => (dispatch) => {
  dispatch({
    type: "PORTFOLIOS_REQUEST_ACCESS",
    payload: requestAccess(portfolioID),
    meta: { portfolioID },
  });
};

export const switchPortfolios = (portfolioIDs) => (dispatch, getState) => {
  const {
    portfolios,
    router: {
      location: { pathname },
    },
  } = getState();

  if (!shouldSwitchPortfolios(getState(), portfolioIDs)) return null;

  let nextPortfolioIDs = intersection(
    portfolios.map((p) => p.id),
    portfolioIDs
  );
  if (!nextPortfolioIDs.length) {
    if (!portfolios.length) return dispatch(push("/no-access"));
    if (portfolios.length) nextPortfolioIDs = portfolios.filter((p) => !p.fake).map((p) => p.id);
  }

  DataStorage.setSelectedPortfolioIDs(nextPortfolioIDs);

  if (
    pathname.startsWith("/deals") ||
    pathname.startsWith("/assets") ||
    pathname.startsWith("/asset_leads")
  ) {
    dispatch(push(getNextPath(pathname, nextPortfolioIDs)));
  }

  return dispatch({
    type: "PORTFOLIO_SWITCH",
    payload: { portfolioIDs: nextPortfolioIDs },
  });
};

export const subscribePortfolio = (portfolioID) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_SUBSCRIBE",
    meta: { portfolioID },
    payload: createSubscription(null, [{ portfolioID }]),
  });

export const unsubscribePortfolio = (portfolioID) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_UNSUBSCRIBE",
    meta: { portfolioID },
    payload: removeSubscription(null, [{ portfolioID }]),
  });

export const openPortfolioModal = (portfolioID) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_MODAL_OPEN",
    meta: { portfolioID },
  });

export const closePortfolioModal = () => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_MODAL_CLOSE",
  });

export const openPortfolioDeleteModal = () => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_DELETE_MODAL_OPEN",
  });

export const closePortfolioDeleteModal = () => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_DELETE_MODAL_CLOSE",
  });

const updatePortfolio = (portfolio) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_UPDATE",
    payload: update(portfolio),
  }).then(() => {
    dispatch(closePortfolioModal());
  });

const createPortfolio = (portfolio) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_CREATE",
    payload: create(portfolio),
  }).then((res) => {
    dispatch(closePortfolioModal());
    dispatch(closePortfolioSidebar());

    return dispatch(switchPortfolios([res.value.portfolio.id]));
  });

export const savePortfolio = (portfolio) => (dispatch) =>
  portfolio.id ? dispatch(updatePortfolio(portfolio)) : dispatch(createPortfolio(portfolio));

export const deletePortfolio = (portfolioID, transferID) => (dispatch) =>
  dispatch({
    type: "PORTFOLIO_DELETE",
    payload: destroy(portfolioID, transferID),
    meta: { portfolioID },
  }).then(() => {
    dispatch(closePortfolioDeleteModal());
    dispatch(closePortfolioModal());
    dispatch(closePortfolioSidebar());
    dispatch(
      showSuccess(
        t("portfolio.delete.success.title"),
        t(`portfolio.delete.success.body${transferID ? "Transfer" : "Delete"}`)
      )
    );
    if (transferID) return dispatch(switchPortfolios([transferID]));

    return dispatch(switchPortfolios([]));
  });
